import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import Login from './pages/Login';
import Legal from './pages/Legal';
import { useAuth } from './hooks/useAuth';
import ApiKeysPage from './pages/api-keys/ApiKeys';
import { CookieNotice } from './components/CookieNotice';
import NotFound from './pages/NotFound';
import { AuthProvider } from './components/auth/AuthProvider';
import { AccessProvider } from './components/access/AccessProvider';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import EmailLink from './pages/EmailLink';
import CliAuthPage from './pages/CliAuth';
import Account from './pages/Account';
import DeveloperLanding from './pages/DeveloperLanding';
import CompleteProfile from './pages/CompleteProfile';
import AccessError from './pages/AccessError';
import {
  PageErrorBoundary,
  AuthErrorBoundary,
  DashboardErrorBoundary,
} from './components/errors/RouteErrorBoundaries';
import { publicRoutes } from './utils/routes';
import { AnalyticsIdentifier } from './components/analytics/AnalyticsIdentifier';
import { TimezoneProvider } from './contexts/TimezoneContext';
import BrowsePage from './pages/browse/Browse';

export function cookieConsentGiven(): string {
  return localStorage.getItem('cookie-consent') ?? 'undecided';
}

function App() {
  const { isAuthenticated, isLoading, needsProfileCompletion } = useAuth();
  const location = useLocation();

  const [consentGiven, setConsentGiven] = useState('');

  const onCookieAccept = () => {
    setConsentGiven('true');
  };

  useEffect(() => {
    setConsentGiven(cookieConsentGiven());
  }, []);

  useEffect(() => {
    if (consentGiven !== '') {
      posthog.set_config({
        persistence: consentGiven === 'true' ? 'localStorage+cookie' : 'memory',
      });
    }
  }, [consentGiven]);

  // Show loading spinner while checking auth state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E34290]"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated and not on a public route
  if (!isAuthenticated && !publicRoutes.includes(location.pathname)) {
    const redirectState = { from: location };
    return <Navigate to="/login" replace state={redirectState} />;
  }

  // If authenticated but needs profile completion, redirect to complete-profile
  if (isAuthenticated && needsProfileCompletion() && location.pathname !== '/complete-profile') {
    return <Navigate to="/complete-profile" replace state={{ from: location }} />;
  }

  return (
    <TimezoneProvider>
      <AuthProvider>
        <AccessProvider>
          <AnalyticsIdentifier />
          <Routes>
            {/* Public routes with auth error boundary */}
            <Route
              path="/login"
              element={
                <AuthErrorBoundary>
                  <Login />
                </AuthErrorBoundary>
              }
            />
            <Route
              path="/auth"
              element={
                <AuthErrorBoundary>
                  <Login />
                </AuthErrorBoundary>
              }
            />
            <Route
              path="/login/confirm"
              element={
                <AuthErrorBoundary>
                  <EmailLink />
                </AuthErrorBoundary>
              }
            />
            <Route
              path="/login/recover"
              element={
                <AuthErrorBoundary>
                  <EmailLink />
                </AuthErrorBoundary>
              }
            />
            <Route
              path="/login/accept-invitation"
              element={
                <AuthErrorBoundary>
                  <EmailLink />
                </AuthErrorBoundary>
              }
            />
            <Route
              path="/login/magic-link"
              element={
                <AuthErrorBoundary>
                  <EmailLink />
                </AuthErrorBoundary>
              }
            />

            {/* Profile completion route - only accessible when authenticated */}
            <Route
              path="/complete-profile"
              element={
                <AuthErrorBoundary>
                  <CompleteProfile />
                </AuthErrorBoundary>
              }
            />

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashboardErrorBoundary>
                    <Layout />
                  </DashboardErrorBoundary>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/overview" replace />} />
              <Route path="overview" element={<DeveloperLanding />} />
              <Route path="api-keys" element={<ApiKeysPage />} />
              <Route path="cli-auth" element={<CliAuthPage />} />
              <Route path="browse" element={<BrowsePage />} />
              <Route path="account" element={<Account />} />
              <Route path="legal" element={<Legal />} />
              <Route path="error" element={<AccessError />} />
            </Route>

            {/* Catch-all route for 404 with page error boundary */}
            <Route
              path="*"
              element={
                <PageErrorBoundary>
                  <NotFound />
                </PageErrorBoundary>
              }
            />
          </Routes>
          <CookieNotice onAccept={onCookieAccept} />
        </AccessProvider>
      </AuthProvider>
    </TimezoneProvider>
  );
}

export default App;
